import React from 'react';
import { visuallyHidden } from '@noths/polaris-client-styles';

import { useIsTruncated } from '../../../hooks/useIsTruncated';
import { StarRating } from '../../atoms/StarRating/StarRating';
import { getRelativeDateLabel } from '../ReviewCard/getRelativeDateLabel';
import { ReviewCardReviewer } from '../ReviewCard/ReviewCardReviewer';
import { READ_MORE, READ_REVIEW } from './copy';
import { getReviewLayout } from './getReviewLayout';
import { ReviewPreviewCardPhotos } from './ReviewPreviewCardPhotos';
import { ReviewPreviewCardReply } from './ReviewPreviewCardReply';
import type { ReviewPreviewCardProps } from './types';
import * as styles from './ReviewPreviewCard.styles';

/**
 * A preview of one customer review: enough of it to be worth reading, and a way through
 * to the rest.
 *
 * The rest is `ReviewCard`, the same review with nothing held back. The two are one
 * design, so what they share is shared as code rather than drawn twice: the rating, the
 * reviewer footer, the partner's logo and the card's own chrome all come from
 * `ReviewCard`. What makes this one the preview is that the review starts clamped and
 * every control on it leads away.
 *
 * Those controls all lead to the same place, the `href` the consumer supplies: "Read
 * more", each photo, "View all photos" and the reply row. That destination is this one
 * review on its own, with the review and the partner's reply both in full, which on a
 * product page is a drawer. The card never expands in place and never opens anything
 * itself.
 *
 * So one component covers both places a preview appears: a row on the product page, and
 * the reviews drawer, where "Read more" opens the single review nested above it. Nothing
 * about the card differs between the two: it fills the height it is given, so a row that
 * stretches its items lines the cards up, and a stacked list leaves each at its own height.
 *
 * All seven Figma variants come out of two facts about a review, whether it has photos
 * and whether the partner replied. `getReviewLayout` decides the lines and photos per
 * shape, and "Read more" takes the line the text gives up, so the text region keeps its
 * lines whether or not the link is showing.
 */
export const ReviewPreviewCard = ({
  date,
  href,
  onControlClick,
  review,
}: ReviewPreviewCardProps) => {
  const dateLabel = getRelativeDateLabel(review.date, date);
  /* Keyed on the text so a card re-rendered with a different review re-measures: the
     `<p>` node persists across the swap, so neither the ref nor a resize would tell the
     hook anything changed. */
  const { isTruncated, ref: textRef } = useIsTruncated(review.text);
  const { maxLines, photoCount, showViewAllPhotos } = getReviewLayout(review);
  /* Only when there is more to read. Never to stand in for something else the card
     cannot show, which is what a link opening to the same two lines would be. */
  const showReadMore = Boolean(review.text) && isTruncated;
  const hasReply = Boolean(review.reply);
  const photos = review.photos?.slice(0, photoCount) ?? [];
  // A card with nothing to click would be a dead end, so the whole card becomes the link.
  const hasControls = showReadMore || hasReply || photos.length > 0;
  /* Built once and placed twice below, because only its position differs: a reply moves
     the photos up beside the text, and without one they sit under it. */
  const photoRow = (
    <ReviewPreviewCardPhotos
      href={href}
      onControlClick={onControlClick}
      photos={photos}
      review={review}
      showViewAllPhotos={showViewAllPhotos}
    />
  );

  return (
    <article
      /* The card has no title of its own to name the region by, so a screen reader
         landing on a row of these has nothing to tell one apart from the next: this
         gives it one. The rating is left out, since `StarRating` already announces it
         on its own, and repeating it here would say the same thing twice. */
      /* Without a readable date there is nothing to qualify the region with, so the
         label says only what the card is. */
      aria-label={dateLabel ? `Customer review, ${dateLabel}` : 'Customer review'}
      css={styles.card}
      data-testid="review-preview-card"
    >
      {!hasControls && (
        <a
          css={styles.cardLink}
          href={href}
          onClick={(e) => onControlClick?.(e, { control: 'card', review })}
        >
          <span css={visuallyHidden}>{READ_REVIEW}</span>
        </a>
      )}
      <div css={styles.content}>
        <div css={styles.header}>
          <div css={styles.ratingRow}>
            <StarRating rating={review.rating} />
            {dateLabel && (
              <time css={styles.date} dateTime={review.date}>
                {dateLabel}
              </time>
            )}
          </div>

          <div css={styles.textRow}>
            <div
              css={styles.textColumn}
              data-testid="review-preview-card-text"
              style={
                {
                  [styles.TEXT_CLAMP_VAR]: showReadMore ? maxLines - 1 : maxLines,
                } as React.CSSProperties
              }
            >
              {review.text && (
                <p css={styles.text} ref={textRef}>
                  {review.text}
                </p>
              )}
              {showReadMore && (
                <a
                  css={styles.readMore}
                  href={href}
                  onClick={(e) => onControlClick?.(e, { control: 'readMore', review })}
                >
                  {READ_MORE}
                </a>
              )}
            </div>
            {hasReply && photoRow}
          </div>

          {!hasReply && photoRow}
        </div>

        <ReviewCardReviewer reviewer={review.reviewer} />
      </div>

      {review.reply && (
        <ReviewPreviewCardReply
          href={href}
          onControlClick={onControlClick}
          reply={review.reply}
          review={review}
        />
      )}
    </article>
  );
};

ReviewPreviewCard.displayName = 'ReviewPreviewCard';
